// PREZENCE — Employee Dashboard (FIXED: real-time work timer that tracks correctly)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const STATUS_BUTTONS = [
  { type:'work',    label:'Work',    icon:'💻', color:'#22c55e', rgb:'34,197,94',   desc:'I am working' },
  { type:'break',   label:'Break',   icon:'☕', color:'#3ba8ff', rgb:'59,168,255',  desc:'Taking a break' },
  { type:'meeting', label:'Meeting', icon:'🎙', color:'#a78bfa', rgb:'167,139,250', desc:'In a meeting' },
];

const fmt = (min) => { const h=Math.floor(min/60), m=min%60; return h>0?`${h}h ${m}m`:`${m}m`; };
const fmtSec = (s) => { const h=Math.floor(s/3600), m=Math.floor((s%3600)/60), sec=s%60; return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`; };

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [switching, setSwitching] = useState(false);
  // Live timer: tracks seconds elapsed in current session
  const [liveSeconds, setLiveSeconds] = useState(0);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/employee');
      setData(res.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh summary every 60 seconds (not the timer — timer runs locally)
  useEffect(() => {
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Live timer — runs locally from session start time, no server needed
  useEffect(() => {
    clearInterval(timerRef.current);
    if (data?.currentSession?.started_at && !data?.attendance?.clock_out) {
      const start = new Date(data.currentSession.started_at);
      const tick = () => setLiveSeconds(Math.floor((Date.now() - start) / 1000));
      tick(); // run immediately
      timerRef.current = setInterval(tick, 1000);
    } else {
      setLiveSeconds(0);
    }
    return () => clearInterval(timerRef.current);
  }, [data?.currentSession?.started_at, data?.attendance?.clock_out]);

  const handleStatus = async (sessionType) => {
    const activeType = data?.currentSession?.session_type;
    if (switching || activeType === sessionType) return;
    setSwitching(true);
    try {
      // Only auto clock-in if not already clocked in AND not clocked out.
      // Guarding on the full attendance state avoids firing a duplicate
      // clock-in that races with the dedicated Clock In button.
      const clockedIn = !!data?.attendance?.clock_in && !data?.attendance?.clock_out;
      if (!clockedIn) {
        await api.post('/attendance/clock-in');
      }
      await api.post('/activity/start', { session_type: sessionType });
      toast.success(`Status → ${sessionType}`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update status');
    } finally { setSwitching(false); }
  };

  const handleClockIn = async () => {
    try {
      const res = await api.post('/attendance/clock-in');
      await api.post('/activity/start', { session_type: 'work' });
      // Show the server's late/on-time message so employees know their status.
      if (res.data?.is_late) toast(res.data.message, { icon: '⏰', duration: 5000 });
      else toast.success(res.data?.message || 'Clocked in! Have a great day 🌅');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.error || 'Clock in failed'); }
  };

  const handleClockOut = async () => {
    try {
      if (data?.currentSession && !data?.currentSession?.ended_at) {
        await api.post('/activity/end');
      }
      await api.post('/attendance/clock-out');
      toast.success('Clocked out. Have a great evening! 👋');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.error || 'Clock out failed'); }
  };

  const currentType = data?.currentSession?.session_type;
  const isClocked   = !!data?.attendance?.clock_in;
  const isClockedOut = isClocked && !!data?.attendance?.clock_out;
  const score   = data?.productivityScore || 0;
  const summary = data?.activitySummary || { work:0, break:0, meeting:0, idle:0 };
  const currentBtn = STATUS_BUTTONS.find(b => b.type === currentType);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:400 }}>
      <div className="spinner" style={{ width:28, height:28 }} />
    </div>
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';

  return (
    <div style={{ animation:'fadeIn 0.4s ease', maxWidth:900, margin:'0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">Good {greeting}, {user?.full_name?.split(' ')[0]}! 👋</h1>
        <p className="page-sub">{new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>
      </div>

      {/* ── STATUS CARD ── */}
      <div className="card" style={{ marginBottom:20, background:'linear-gradient(135deg,rgba(13,21,32,0.95),rgba(10,16,26,0.98))' }}>
        {!isClocked ? (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🌅</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:'#f0f6ff', marginBottom:8 }}>Ready to start your day?</h2>
            <p style={{ fontSize:13, color:'#64748b', marginBottom:24 }}>Click Clock In to begin tracking your work</p>
            <button className="btn btn-primary btn-lg" onClick={handleClockIn}>🟢 Clock In Now</button>
          </div>
        ) : isClockedOut ? (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🌙</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:'#f0f6ff', marginBottom:8 }}>Day Complete!</h2>
            <p style={{ fontSize:13, color:'#64748b', marginBottom:4 }}>
              You worked for <span style={{ color:'#63dcbc', fontWeight:700 }}>{data?.attendance?.total_hours}h</span> today
            </p>
            <p style={{ fontSize:12, color:'#475569' }}>
              Clocked out at {new Date(data?.attendance?.clock_out).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
            </p>
          </div>
        ) : (
          <>
            {/* Current status + LIVE TIMER */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
              <div>
                <div style={{ fontSize:11, color:'#475569', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:8 }}>CURRENT STATUS</div>
                {currentType && currentBtn ? (
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:currentBtn.color, boxShadow:`0 0 10px ${currentBtn.color}`, animation:'pulse-dot 2s infinite' }} />
                    <span style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:currentBtn.color }}>
                      {currentBtn.label}
                    </span>
                    {/* LIVE TIMER — counts up from session start */}
                    <span style={{ fontFamily:"'JetBrains Mono'", fontSize:20, color:'#63dcbc', fontWeight:700, letterSpacing:'0.05em' }}>
                      {fmtSec(liveSeconds)}
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize:15, color:'#64748b' }}>No status — click a button below</span>
                )}
                {/* Clock in time */}
                <div style={{ fontSize:11, color:'#334155', fontFamily:"'JetBrains Mono'", marginTop:6 }}>
                  Clocked in at {data?.attendance?.clock_in ? new Date(data.attendance.clock_in).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '--:--'}
                </div>
              </div>
              <button className="btn btn-danger btn-sm" onClick={handleClockOut}>Clock Out</button>
            </div>

            {/* Status buttons */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
              {STATUS_BUTTONS.map(btn => {
                const isActive = currentType === btn.type;
                return (
                  <button key={btn.type} onClick={() => handleStatus(btn.type)} disabled={switching} style={{
                    background: isActive ? `rgba(${btn.rgb},0.15)` : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${isActive ? btn.color : 'rgba(255,255,255,0.08)'}`,
                    borderRadius:14, padding:'18px 14px',
                    cursor: switching ? 'not-allowed' : 'pointer',
                    textAlign:'center', transition:'all 0.2s',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isActive ? `0 0 20px rgba(${btn.rgb},0.2)` : 'none',
                  }}>
                    <div style={{ fontSize:26, marginBottom:7 }}>{btn.icon}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:isActive?btn.color:'#94a3b8', marginBottom:3, fontFamily:"'DM Sans',sans-serif" }}>{btn.label}</div>
                    <div style={{ fontSize:11, color:'#475569' }}>{btn.desc}</div>
                    {isActive && (
                      <div style={{ marginTop:8 }}>
                        <span className="badge" style={{ background:`rgba(${btn.rgb},0.2)`, color:btn.color, border:'none', fontSize:10 }}>● ACTIVE</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom:20 }}>
        {/* Score */}
        <div className="card" style={{ textAlign:'center' }}>
          <div style={{ fontSize:10, color:'#475569', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:12 }}>PRODUCTIVITY</div>
          <div style={{ position:'relative', width:72, height:72, margin:'0 auto 10px' }}>
            <svg viewBox="0 0 72 72" style={{ transform:'rotate(-90deg)' }}>
              <circle cx="36" cy="36" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
              <circle cx="36" cy="36" r="28" fill="none" stroke={score>=80?'#22c55e':score>=60?'#3ba8ff':'#f59e0b'} strokeWidth="8"
                strokeDasharray={`${(score/100)*176} 176`} strokeLinecap="round" style={{ transition:'stroke-dasharray 1s ease' }} />
            </svg>
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'JetBrains Mono'", fontSize:17, fontWeight:700, color:score>=80?'#22c55e':score>=60?'#3ba8ff':'#f59e0b' }}>{score}%</div>
          </div>
          <div style={{ fontSize:11, color:'#64748b' }}>Today's score</div>
        </div>

        {[
          { label:'Work Time',   value:fmt(summary.work),    color:'#22c55e' },
          { label:'Break Time',  value:fmt(summary.break),   color:'#3ba8ff' },
          { label:'Meetings',    value:fmt(summary.meeting), color:'#a78bfa' },
        ].map((st,i) => (
          <div key={i} className="card" style={{ textAlign:'center' }}>
            <div style={{ fontSize:10, color:'#475569', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:12 }}>{st.label.toUpperCase()}</div>
            <div style={{ fontSize:28, fontWeight:800, color:st.color, fontFamily:"'JetBrains Mono'", marginBottom:4 }}>{st.value}</div>
            <div style={{ fontSize:11, color:'#64748b' }}>{st.label.toLowerCase()}</div>
          </div>
        ))}
      </div>

      {/* Leave balance */}
      {data?.leaveBalance?.length > 0 && (
        <div className="card" style={{ marginBottom:20 }}>
          <div className="card-header"><span className="card-title">LEAVE BALANCE</span></div>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
            {data.leaveBalance.map((lb,i) => (
              <div key={i} style={{ flex:'1 1 130px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:11, color:'#475569', marginBottom:6 }}>{lb.leave_types?.name}</div>
                <div style={{ fontSize:24, fontWeight:800, color:lb.leave_types?.color||'#63dcbc', fontFamily:"'JetBrains Mono'" }}>{lb.balance}</div>
                <div style={{ fontSize:10, color:'#334155' }}>days remaining</div>
                <div className="progress-bar-wrap" style={{ marginTop:8 }}>
                  <div className="progress-bar-fill" style={{ width:`${lb.allocated>0?(lb.balance/lb.allocated)*100:0}%`, background:lb.leave_types?.color||'#63dcbc' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent leaves */}
      {data?.myLeaves?.length > 0 && (
        <div className="card">
          <div className="card-header"><span className="card-title">RECENT LEAVE APPLICATIONS</span></div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {data.myLeaves.map(leave => (
              <div key={leave.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#e2e8f0' }}>{leave.leave_types?.name}</div>
                  <div style={{ fontSize:11, color:'#475569' }}>
                    {new Date(leave.from_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})} — {new Date(leave.to_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})} · {leave.total_days} day{leave.total_days>1?'s':''}
                  </div>
                </div>
                <span className={`badge badge-${leave.status==='approved'?'green':leave.status==='rejected'?'red':leave.status==='pending'?'amber':'gray'}`}>
                  {leave.status.charAt(0).toUpperCase()+leave.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
